/**
 * NVD (National Vulnerability Database) API v2 client
 * Fetches real CVE data including CVSS scores, descriptions, and references.
 * Results are cached in localStorage for 24 hours to minimize API calls.
 */

const NVD_API_BASE = "https://services.nvd.nist.gov/rest/json/cves/2.0";
const NVD_API_KEY = import.meta.env.VITE_NVD_API_KEY || "";
const CACHE_PREFIX = "nvd-cve-";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export interface NvdCveData {
  cveId: string;
  description: string;
  cvssScore: number | null;
  cvssVector: string | null;
  cvssVersion: string | null;
  severity: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW" | "NONE";
  publishedDate: string | null;
  lastModifiedDate: string | null;
  references: { url: string; source: string }[];
  weaknesses: string[];
}

interface CacheEntry {
  data: NvdCveData;
  ts: number;
}

function readCache(cveId: string): NvdCveData | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + cveId);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > CACHE_TTL) {
      localStorage.removeItem(CACHE_PREFIX + cveId);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache(cveId: string, data: NvdCveData) {
  try {
    localStorage.setItem(CACHE_PREFIX + cveId, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Storage full — silently fail
  }
}

function extractCvssData(metrics: any): { score: number | null; vector: string | null; version: string | null; severity: NvdCveData["severity"] } {
  // Prefer CVSS v3.1, then v3.0, then v2
  const v31 = metrics?.cvssMetricV31?.[0]?.cvssData;
  if (v31) {
    return {
      score: v31.baseScore,
      vector: v31.vectorString,
      version: "3.1",
      severity: mapSeverity(v31.baseScore),
    };
  }

  const v30 = metrics?.cvssMetricV30?.[0]?.cvssData;
  if (v30) {
    return {
      score: v30.baseScore,
      vector: v30.vectorString,
      version: "3.0",
      severity: mapSeverity(v30.baseScore),
    };
  }

  const v2 = metrics?.cvssMetricV2?.[0]?.cvssData;
  if (v2) {
    return {
      score: v2.baseScore,
      vector: v2.vectorString,
      version: "2.0",
      severity: mapSeverity(v2.baseScore),
    };
  }

  return { score: null, vector: null, version: null, severity: "NONE" };
}

function mapSeverity(score: number): NvdCveData["severity"] {
  if (score >= 9.0) return "CRITICAL";
  if (score >= 7.0) return "HIGH";
  if (score >= 4.0) return "MEDIUM";
  if (score > 0) return "LOW";
  return "NONE";
}

function parseCveItem(item: any): NvdCveData {
  const cve = item.cve;
  const descriptions = cve.descriptions || [];
  const enDesc = descriptions.find((d: any) => d.lang === "en")?.value || "";

  const cvss = extractCvssData(cve.metrics);
  const refs = (cve.references || []).map((r: any) => ({ url: r.url, source: r.source || "" }));
  const weaknesses = (cve.weaknesses || [])
    .flatMap((w: any) => w.description || [])
    .filter((d: any) => d.lang === "en")
    .map((d: any) => d.value);

  return {
    cveId: cve.id,
    description: enDesc,
    cvssScore: cvss.score,
    cvssVector: cvss.vector,
    cvssVersion: cvss.version,
    severity: cvss.severity,
    publishedDate: cve.published || null,
    lastModifiedDate: cve.lastModified || null,
    references: refs,
    weaknesses,
  };
}

// Rate limiting: NVD allows 50 req/30s with key, 5 req/30s without
const requestQueue: (() => void)[] = [];
let activeRequests = 0;
const MAX_CONCURRENT = NVD_API_KEY ? 8 : 2;
const REQUEST_DELAY = NVD_API_KEY ? 600 : 6000; // ms between requests

function enqueueRequest<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const execute = async () => {
      activeRequests++;
      try {
        const result = await fn();
        resolve(result);
      } catch (e) {
        reject(e);
      } finally {
        activeRequests--;
        setTimeout(() => {
          const next = requestQueue.shift();
          if (next) next();
        }, REQUEST_DELAY);
      }
    };

    if (activeRequests < MAX_CONCURRENT) {
      execute();
    } else {
      requestQueue.push(execute);
    }
  });
}

/**
 * Fetch a single CVE from NVD API v2.
 * Returns cached data if available.
 */
export async function fetchCveFromNvd(cveId: string): Promise<NvdCveData | null> {
  // Check cache first
  const cached = readCache(cveId);
  if (cached) return cached;

  return enqueueRequest(async () => {
    // Double-check cache (another request may have populated it)
    const cached2 = readCache(cveId);
    if (cached2) return cached2;

    try {
      const headers: Record<string, string> = {};
      if (NVD_API_KEY) {
        headers["apiKey"] = NVD_API_KEY;
      }

      const res = await fetch(`${NVD_API_BASE}?cveId=${cveId}`, { headers });

      if (res.status === 403 || res.status === 429) {
        console.warn(`NVD rate limited for ${cveId}, will retry later`);
        return null;
      }

      if (!res.ok) {
        console.warn(`NVD API error for ${cveId}: ${res.status}`);
        return null;
      }

      const json = await res.json();
      const vulnerabilities = json.vulnerabilities || [];
      if (vulnerabilities.length === 0) return null;

      const data = parseCveItem(vulnerabilities[0]);
      writeCache(cveId, data);
      return data;
    } catch (e) {
      console.warn(`Failed to fetch NVD data for ${cveId}:`, e);
      return null;
    }
  });
}

const SEARCH_CACHE_PREFIX = "nvd-search-";
const SEARCH_CACHE_TTL = 24 * 60 * 60 * 1000;

interface SearchCacheEntry {
  data: NvdCveData[];
  ts: number;
}

function readSearchCache(key: string): NvdCveData[] | null {
  try {
    const raw = localStorage.getItem(SEARCH_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: SearchCacheEntry = JSON.parse(raw);
    if (Date.now() - entry.ts > SEARCH_CACHE_TTL) {
      localStorage.removeItem(SEARCH_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function writeSearchCache(key: string, data: NvdCveData[]) {
  try {
    localStorage.setItem(SEARCH_CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // ignore
  }
}

async function nvdSearch(params: Record<string, string>, cacheKey: string, limit = 20): Promise<NvdCveData[]> {
  const cached = readSearchCache(cacheKey);
  if (cached) return cached;

  return enqueueRequest(async () => {
    const cached2 = readSearchCache(cacheKey);
    if (cached2) return cached2;

    try {
      const headers: Record<string, string> = {};
      if (NVD_API_KEY) headers["apiKey"] = NVD_API_KEY;

      const qs = new URLSearchParams({ ...params, resultsPerPage: String(limit) }).toString();
      const res = await fetch(`${NVD_API_BASE}?${qs}`, { headers });

      if (res.status === 403 || res.status === 429) {
        console.warn(`NVD rate limited for search ${cacheKey}`);
        return [];
      }
      if (!res.ok) {
        console.warn(`NVD API error for search ${cacheKey}: ${res.status}`);
        return [];
      }

      const json = await res.json();
      const items = (json.vulnerabilities || []).map(parseCveItem);
      writeSearchCache(cacheKey, items);
      // Also opportunistically cache individual CVEs
      for (const item of items) writeCache(item.cveId, item);
      return items;
    } catch (e) {
      console.warn(`NVD search failed for ${cacheKey}:`, e);
      return [];
    }
  });
}

/**
 * Search CVEs by CPE 2.3 match string (e.g. cpe:2.3:a:google:chrome:120.0.0:*:*:*:*:*:*:*).
 * Uses virtualMatchString so partial CPE strings work.
 */
export async function searchCvesByCpe(cpeMatchString: string, limit = 20): Promise<NvdCveData[]> {
  return nvdSearch({ virtualMatchString: cpeMatchString }, `cpe:${cpeMatchString}:${limit}`, limit);
}

/**
 * Search CVEs by keyword (vendor/product) with optional version refinement client-side.
 */
export async function searchCvesByKeyword(keyword: string, limit = 20): Promise<NvdCveData[]> {
  return nvdSearch(
    { keywordSearch: keyword, keywordExactMatch: "" },
    `kw:${keyword.toLowerCase()}:${limit}`,
    limit,
  );
}

/**
 * Batch fetch CVEs, returning a map of cveId -> NvdCveData.
 * Prioritizes cache and rate-limits network requests.
 */
export async function fetchCveBatch(cveIds: string[]): Promise<Map<string, NvdCveData>> {
  const results = new Map<string, NvdCveData>();
  const toFetch: string[] = [];

  // Check cache first
  for (const id of cveIds) {
    const cached = readCache(id);
    if (cached) {
      results.set(id, cached);
    } else {
      toFetch.push(id);
    }
  }

  // Fetch remaining in parallel (rate-limited)
  if (toFetch.length > 0) {
    const promises = toFetch.map(async (id) => {
      const data = await fetchCveFromNvd(id);
      if (data) results.set(id, data);
    });
    await Promise.allSettled(promises);
  }

  return results;
}
